import { useCallback, useEffect, useRef, useState } from 'react';
import { useJamesPanelContext, useJamesThreadContext } from '../../context/JamesContext';
import { JamesChat } from './JamesChat';
import { JamesErrorBoundary } from './JamesErrorBoundary';
import clsx from 'clsx';

export type JamesDockMode = 'right' | 'bottom' | 'floating';

interface LayoutState {
  mode: JamesDockMode;
  right: { width: number };
  bottom: { height: number };
  floating: { x: number; y: number; width: number; height: number };
}

const STORAGE_KEY = 'james-panel-layout';

const DEFAULT_LAYOUT: LayoutState = {
  mode: 'right',
  right: { width: 448 },
  bottom: { height: 420 },
  floating: { x: 96, y: 96, width: 560, height: 640 },
};

const MIN_W_RIGHT = 320;
const MIN_H_BOTTOM = 240;
const MIN_W_FLOAT = 320;
const MIN_H_FLOAT = 240;

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v));
}

function loadLayout(): LayoutState {
  const base = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return DEFAULT_LAYOUT;
      const parsed = JSON.parse(raw);
      return {
        mode: parsed?.mode === 'right' || parsed?.mode === 'bottom' || parsed?.mode === 'floating' ? parsed.mode : DEFAULT_LAYOUT.mode,
        right: { width: Number(parsed?.right?.width) || DEFAULT_LAYOUT.right.width },
        bottom: { height: Number(parsed?.bottom?.height) || DEFAULT_LAYOUT.bottom.height },
        floating: {
          x: Number(parsed?.floating?.x) || DEFAULT_LAYOUT.floating.x,
          y: Number(parsed?.floating?.y) || DEFAULT_LAYOUT.floating.y,
          width: Number(parsed?.floating?.width) || DEFAULT_LAYOUT.floating.width,
          height: Number(parsed?.floating?.height) || DEFAULT_LAYOUT.floating.height,
        },
      } as LayoutState;
    } catch {
      return DEFAULT_LAYOUT;
    }
  })();

  // Clamp all stored dims against the current viewport so the input bar
  // can never end up below the viewport on load.
  if (typeof window === 'undefined') return base;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rightWidth = clamp(base.right.width, MIN_W_RIGHT, Math.round(vw * 0.8));
  const bottomHeight = clamp(base.bottom.height, MIN_H_BOTTOM, Math.round(vh * 0.8));
  const floatWidth = clamp(base.floating.width, MIN_W_FLOAT, Math.round(vw * 0.95));
  const floatHeight = clamp(base.floating.height, MIN_H_FLOAT, Math.round(vh * 0.9));
  const floatX = clamp(base.floating.x, 0, Math.max(0, vw - floatWidth));
  const floatY = clamp(base.floating.y, 0, Math.max(0, vh - floatHeight));
  return {
    mode: base.mode,
    right: { width: rightWidth },
    bottom: { height: bottomHeight },
    floating: { x: floatX, y: floatY, width: floatWidth, height: floatHeight },
  };
}

function saveLayout(layout: LayoutState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // ignore
  }
}

type DragKind = 'resize-right' | 'resize-bottom' | 'resize-float-edge' | 'move-float';
interface DragState {
  kind: DragKind;
  startX: number;
  startY: number;
  start: { width?: number; height?: number; x?: number; y?: number };
  edges?: { left: boolean; right: boolean; top: boolean; bottom: boolean };
}

export function JamesPanel() {
  const { isPanelOpen, closePanel } = useJamesPanelContext();
  const { activeThreadId, setActiveThreadId } = useJamesThreadContext();

  const [layout, setLayout] = useState<LayoutState>(loadLayout);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const dragRef = useRef<DragState | null>(null);

  const setMode = useCallback((mode: JamesDockMode) => {
    setLayout((prev) => {
      const next = { ...prev, mode };
      saveLayout(next);
      return next;
    });
  }, []);

  const maxWRight = () => Math.round(window.innerWidth * 0.8);
  const maxHBottom = () => Math.round(window.innerHeight * 0.8);
  const maxWFloat = () => Math.round(window.innerWidth * 0.95);
  const maxHFloat = () => Math.round(window.innerHeight * 0.9);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    setLayout((prev) => {
      switch (d.kind) {
        case 'resize-right': {
          const w = clamp((d.start.width ?? 0) - dx, MIN_W_RIGHT, maxWRight());
          return { ...prev, right: { width: w } };
        }
        case 'resize-bottom': {
          const h = clamp((d.start.height ?? 0) - dy, MIN_H_BOTTOM, maxHBottom());
          return { ...prev, bottom: { height: h } };
        }
        case 'move-float': {
          const f = prev.floating;
          const x = clamp((d.start.x ?? 0) + dx, 0, Math.max(0, window.innerWidth - f.width));
          const y = clamp((d.start.y ?? 0) + dy, 0, Math.max(0, window.innerHeight - f.height));
          return { ...prev, floating: { ...f, x, y } };
        }
        case 'resize-float-edge': {
          const edges = d.edges!;
          const startX = d.start.x ?? 0;
          const startY = d.start.y ?? 0;
          const startW = d.start.width ?? 0;
          const startH = d.start.height ?? 0;
          let width = startW;
          let height = startH;
          let x = startX;
          let y = startY;
          if (edges.right) width = startW + dx;
          if (edges.bottom) height = startH + dy;
          if (edges.left) width = startW - dx;
          if (edges.top) height = startH - dy;
          width = clamp(width, MIN_W_FLOAT, maxWFloat());
          height = clamp(height, MIN_H_FLOAT, maxHFloat());
          if (edges.left) x = startX + (startW - width);
          if (edges.top) y = startY + (startH - height);
          x = clamp(x, 0, Math.max(0, window.innerWidth - width));
          y = clamp(y, 0, Math.max(0, window.innerHeight - height));
          return { ...prev, floating: { x, y, width, height } };
        }
        default:
          return prev;
      }
    });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (dragRef.current) {
      try {
        (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
      } catch {
        // no-op
      }
      dragRef.current = null;
      saveLayout(layoutRef.current);
    }
  }, []);

  const onResizeRightPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      kind: 'resize-right',
      startX: e.clientX,
      startY: e.clientY,
      start: { width: layoutRef.current.right.width },
    };
  }, []);

  const onResizeBottomPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      kind: 'resize-bottom',
      startX: e.clientX,
      startY: e.clientY,
      start: { height: layoutRef.current.bottom.height },
    };
  }, []);

  const makeFloatEdgeDown = (edges: { left: boolean; right: boolean; top: boolean; bottom: boolean }) =>
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
      const f = layoutRef.current.floating;
      dragRef.current = {
        kind: 'resize-float-edge',
        startX: e.clientX,
        startY: e.clientY,
        start: { x: f.x, y: f.y, width: f.width, height: f.height },
        edges,
      };
    };

  const onFloatingOuterPointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, input, textarea, a, [data-resize-handle]')) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Only grab from the top ~88px (chat header zone)
    if (e.clientY - rect.top > 88) return;
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const f = layoutRef.current.floating;
    dragRef.current = {
      kind: 'move-float',
      startX: e.clientX,
      startY: e.clientY,
      start: { x: f.x, y: f.y },
    };
  }, []);

  // Keep floating panel in bounds if the viewport shrinks
  useEffect(() => {
    const onResize = () => {
      setLayout((prev) => {
        if (prev.mode !== 'floating') return prev;
        const f = prev.floating;
        const width = clamp(f.width, MIN_W_FLOAT, maxWFloat());
        const height = clamp(f.height, MIN_H_FLOAT, maxHFloat());
        const x = clamp(f.x, 0, Math.max(0, window.innerWidth - width));
        const y = clamp(f.y, 0, Math.max(0, window.innerHeight - height));
        if (width === f.width && height === f.height && x === f.x && y === f.y) return prev;
        return { ...prev, floating: { x, y, width, height } };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const chat = (
    <JamesErrorBoundary onClose={closePanel}>
      <JamesChat
        threadId={activeThreadId}
        onThreadChange={setActiveThreadId}
        onClose={closePanel}
        showClose={true}
        compact={true}
        dockMode={layout.mode}
        onDockModeChange={setMode}
      />
    </JamesErrorBoundary>
  );

  if (layout.mode === 'right') {
    // Docked under the 56px header, not over it: the account menu stays
    // reachable, and its dropdown (z-50) opens above the panel (z-40).
    return (
      <div
        className={clsx(
          'fixed bottom-0 right-0 top-14 z-40 flex transform flex-col border-l border-border-light bg-surface-primary shadow-strong transition-transform duration-300 ease-in-out',
          isPanelOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{ width: layout.right.width, maxWidth: '100vw' }}
      >
        <div
          data-resize-handle
          onPointerDown={onResizeRightPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="absolute inset-y-0 left-0 z-20 w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-text-tertiary/30"
          aria-label="Resize panel"
        />
        {chat}
      </div>
    );
  }

  if (layout.mode === 'bottom') {
    return (
      <div
        className={clsx(
          'fixed inset-x-0 bottom-0 z-40 flex transform flex-col border-t border-border-light bg-surface-primary shadow-strong transition-transform duration-300 ease-in-out',
          isPanelOpen ? 'translate-y-0' : 'translate-y-full'
        )}
        style={{ height: layout.bottom.height, maxHeight: '100vh' }}
      >
        <div
          data-resize-handle
          onPointerDown={onResizeBottomPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="absolute inset-x-0 top-0 z-20 h-1.5 -translate-y-1/2 cursor-row-resize hover:bg-text-tertiary/30"
          aria-label="Resize panel"
        />
        {chat}
      </div>
    );
  }

  const f = layout.floating;
  return (
    <div
      onPointerDown={onFloatingOuterPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className={clsx(
        'fixed z-40 flex flex-col overflow-hidden rounded-xl border border-border-light bg-surface-primary shadow-strong transition-opacity duration-200',
        isPanelOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      )}
      style={{
        left: Math.max(0, f.x),
        top: Math.max(0, f.y),
        width: f.width,
        height: f.height,
        maxWidth: `calc(100vw - ${Math.max(0, f.x)}px)`,
        maxHeight: `calc(100vh - ${Math.max(0, f.y)}px)`,
      }}
    >
      <div
        data-resize-handle
        onPointerDown={makeFloatEdgeDown({ left: true, right: false, top: false, bottom: false })}
        className="absolute inset-y-0 left-0 z-20 w-1.5 cursor-ew-resize hover:bg-text-tertiary/30"
      />
      <div
        data-resize-handle
        onPointerDown={makeFloatEdgeDown({ left: false, right: true, top: false, bottom: false })}
        className="absolute inset-y-0 right-0 z-20 w-1.5 cursor-ew-resize hover:bg-text-tertiary/30"
      />
      <div
        data-resize-handle
        onPointerDown={makeFloatEdgeDown({ left: false, right: false, top: true, bottom: false })}
        className="absolute inset-x-0 top-0 z-20 h-1.5 cursor-ns-resize hover:bg-text-tertiary/30"
      />
      <div
        data-resize-handle
        onPointerDown={makeFloatEdgeDown({ left: false, right: false, top: false, bottom: true })}
        className="absolute inset-x-0 bottom-0 z-20 h-1.5 cursor-ns-resize hover:bg-text-tertiary/30"
      />
      <div
        data-resize-handle
        onPointerDown={makeFloatEdgeDown({ left: false, right: true, top: false, bottom: true })}
        className="absolute bottom-0 right-0 z-30 h-3 w-3 cursor-nwse-resize"
      />
      <div
        data-resize-handle
        onPointerDown={makeFloatEdgeDown({ left: true, right: false, top: false, bottom: true })}
        className="absolute bottom-0 left-0 z-30 h-3 w-3 cursor-nesw-resize"
      />
      {chat}
    </div>
  );
}
